/**
 * SDK constants and configuration values.
 *
 * @remarks
 * This module contains constant values used throughout the BD SDK, including
 * default API endpoints, configuration values, and other immutable settings.
 * These constants provide centralized configuration management and ensure
 * consistency across the SDK.
 *
 * @module Constants
 * @category Internal
 * @packageDocumentation
 *
 * @example
 * ```typescript
 * import { DEFAULT_BASE_PATH, DEFAULT_RPC_BASE_PATH } from "@blockdaemon/bd-sdk";
 *
 * // Use the constants to initialize the SDK or build custom endpoints:
 * const sdk = new BdSdk({
 *   bdApiKey: "your-api-key",
 *   basePath: DEFAULT_BASE_PATH
 * });
 * const ethRpcUrl = `${DEFAULT_RPC_BASE_PATH}/ethereum/mainnet/native/http-rpc`;
 * ```
 */
/**
 * Default base URL for Blockdaemon API requests.
 *
 * This constant defines the production API endpoint for Blockdaemon services.
 * It's used as the default base path when no custom endpoint is specified
 * during SDK initialization.
 *
 * @remarks
 * This is the production endpoint. For development or testing, you may want
 * to use a different endpoint by providing a custom basePath in the SDK
 * configuration.
 *
 * @example
 * ```typescript
 * import { DEFAULT_BASE_PATH } from "@blockdaemon/bd-sdk";
 *
 * // Use default production endpoint
 * const sdk = new BdSdk({
 *   bdApiKey: "your-api-key"
 *   // basePath defaults to DEFAULT_BASE_PATH
 * });
 *
 * // Override with custom endpoint
 * const customSdk = new BdSdk({
 *   bdApiKey: "your-api-key",
 *   basePath: "https://my-proxy.example.com"
 * });
 * ```
 *
 * @internal
 */
/**
 * Default base URL for REST API endpoints (e.g. staking, defi).
 */
export const DEFAULT_BASE_PATH = "https://svc.blockdaemon.com";
/**
 * Default base URL for JSON-RPC endpoints.
 */
export const DEFAULT_RPC_BASE_PATH = "https://svc.blockdaemon.com";
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiY29uc3RhbnRzLmpzIiwic291cmNlUm9vdCI6IiIsInNvdXJjZXMiOlsiLi4vLi4vLi4vLi4vc3JjL21haW4vc2hhcmVkL2NvbnN0YW50cy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFBQTs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7O0dBd0JHO0FBRUg7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7OztHQThCRztBQUNIOztHQUVHO0FBQ0gsTUFBTSxDQUFDLE1BQU0saUJBQWlCLEdBQUcsNkJBQTZCLENBQUM7QUFFL0Q7O0dBRUc7QUFDSCxNQUFNLENBQUMsTUFBTSxxQkFBcUIsR0FBRyw2QkFBNkIsQ0FBQyJ9